/**
 * EmbedEntry — entry point when the Games Frontend is opened from the SC Mobile App.
 *
 * The SC Backend signs the payload using Telegram-style hash validation:
 *   data_check_string = fields sorted by key, joined as "key=value\n..."
 *   hash = HMAC-SHA256(data_check_string, SIGNATURE_KEY)
 *
 * The mobile app opens the WebView at:
 *   https://games.example.com/embed?userId=…&displayName=…&gameId=…&ts=…&hash=…&embed=true
 *
 * This component:
 *   1. Reads all signed params from the URL query string.
 *   2. POSTs them to POST /api/auth/exchange for hash validation on the server.
 *   3. Stores the returned player identity + Games-Backend JWT.
 *   4. Navigates to /room/:roomId.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlayerStore } from '@/store/player.js';
import { apiFetch } from '@/platform/api.js';

interface ExchangeResponse {
  token: string;
  playerId: string;
  displayName: string;
  roomId: string;
}

export function EmbedEntry() {
  const navigate = useNavigate();
  const { setPlayerId, setDisplayName, setAvatarUrl, setToken } = usePlayerStore();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const userId = params.get('userId');
    const displayName = params.get('displayName');
    const gameId = params.get('gameId');
    const ts = params.get('ts');
    const hash = params.get('hash');

    // Optional and unsigned: the SC app's profile picture for this player. It
    // is deliberately NOT part of the HMAC payload, so app builds that don't
    // send it (and the hash they compute today) keep working unchanged.
    const rawAvatar = (params.get('avatarUrl') || params.get('profilePic') || params.get('avatar') || '').trim();
    const avatarUrl = /^(https?:\/\/|data:image\/)/i.test(rawAvatar) ? rawAvatar : null;

    if (!userId || !displayName || !gameId || !ts || !hash) {
      setError('Missing required parameters. Please open this page from the Social Casino app.');
      return;
    }

    apiFetch('/api/auth/exchange', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, displayName, gameId, ts: Number(ts), hash }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({})) as { error?: string };
          throw new Error(body.error ?? `Server error (${res.status})`);
        }
        return res.json() as Promise<ExchangeResponse>;
      })
      .then((data) => {
        setPlayerId(data.playerId);
        setDisplayName(data.displayName);
        setAvatarUrl(avatarUrl);
        setToken(data.token);
        navigate(`/room/${data.roomId}`, { replace: true });
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Failed to authenticate';
        setError(msg);
      });
  // Run once on mount — deps are stable store setters
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="flex min-h-dvh items-center justify-center p-6">
        <div className="max-w-sm text-center">
          <p className="text-lg font-semibold text-red-600 dark:text-red-400">Unable to start game</p>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-indigo-600" />
        <p className="text-sm text-gray-500 dark:text-gray-400">Starting game…</p>
      </div>
    </div>
  );
}
